/**
 * PL(階層版。2026-09-10 指示で採用)。
 *  縦軸: 売上 → 会社 → 品川／在宅 → 項目 の階層(行クリックで1段ずつ開く)
 *  横軸: 日 ／ 週 ／ 月(画面上部の切替ボタン)。期間ツールバーの範囲を、選んだ単位で区切って列にし、一番右に合計を出す
 * 計算のルールは①(1日シート版)と同じ: 取得件数は日報/サマリの手修正が優先、人件費・交通費も入力があればその値。
 * 売上 = 納品件数 × その日の会社単価。キャンセル分・経費・通話料は列(期間)ごとに全体で持つ。
 */
import { useEffect, useMemo, useState } from 'react';
import HelpTip from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import { weekdayLabel } from '@core/lib/date';
import { COST_STYLES, DELIVERY_STYLES, priceOn, type Company, type CostStyle, type DateStr, type Delivery, type DeliveryStyle, type MonthTargets } from '@core/lib/types';
import { apptOverrideMap, deliveryStyleOf, type StaffMetrics } from './calc';
import type { ExpenseDaily, ExpenseItem } from './expenseRepo';
import { costStyleOf } from './deliveryEdit';
import { HELP, costRateClass } from './colors';
import { fmtCellUnit, MONEY_UNIT_LABELS, type MoneyUnit, type PlRow } from './plRow';

export type Gran = 'day' | 'week' | 'month';
export const GRAN_LABELS: Record<Gran, string> = { day: '日ごと', week: '週ごと', month: '月ごと' };
type Branch = 'shinagawa' | 'zaitaku';

type Bucket = { key: string; label: string; dates: DateStr[] };

/**
 * 列(横軸)の作り方(2026-09-10 指示):
 *  - 日ごと: その月の1日〜末日 を1日ずつ / 週ごと: その週の月〜日 を1日ずつ / 月ごと: その年の1月〜12月
 * (期間そのものは画面上部の切替に合わせて 月/週/年 になっている)
 */
function makeBuckets(dates: DateStr[], gran: Gran): Bucket[] {
  const out: Bucket[] = [];
  const byKey = new Map<string, Bucket>();
  for (const d of dates) {
    const key = gran === 'month' ? d.slice(0, 7) : d;
    const label = gran === 'month' ? `${Number(d.slice(5, 7))}月` : gran === 'week' ? `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}` : `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
    const b = byKey.get(key);
    if (b) b.dates.push(d);
    else {
      const nb = { key, label, dates: [d] };
      byKey.set(key, nb);
      out.push(nb);
    }
  }
  return out;
}

/** 会社×支店の1マス */
type Cell = { appts: number; apptsBy: Record<DeliveryStyle, number>; delivered: number; sales: number; cost: Partial<Record<CostStyle, number>>; commute: number; timeeFee: number };
const emptyCell = (): Cell => ({ appts: 0, apptsBy: { shinagawa: 0, buai: 0, jikyu: 0 }, delivered: 0, sales: 0, cost: {}, commute: 0, timeeFee: 0 });

export default function PlTreeView({
  rows,
  deliveries,
  companies,
  targets,
  dates,
  gran,
  expenses,
  items,
  unit,
}: {
  rows: StaffMetrics[];
  deliveries: Delivery[];
  companies: Company[];
  targets: MonthTargets;
  dates: DateStr[];
  /** 横軸の単位(画面上部の切替) */
  gran: Gran;
  /** 金額の単位(円／千円／万円。画面上部の切替) */
  unit: MoneyUnit;
  /** 日ごとの経費(日報/サマリの経費画面。P-22 で PL はこれだけを使う) */
  expenses: ExpenseDaily;
  /** 経費の項目(並び順もこのとおり) */
  items: ExpenseItem[];
}) {
  /** 既定は2階層目まで開く(親の直下の行が見えている状態。2026-09-11 指示) */
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);
  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      setTouched(true);
      return next;
    });

  const buckets = useMemo(() => makeBuckets(dates, gran), [dates, gran]);

  const model = useMemo(() => {
    const bucketOf = new Map<DateStr, string>();
    for (const b of buckets) for (const d of b.dates) bucketOf.set(d, b.key);
    const keys = buckets.map((b) => b.key);
    const zero = () => Object.fromEntries(keys.map((k) => [k, 0])) as Record<string, number>;

    // 会社 → 支店 → 期間 のマス
    const cells = new Map<string, Cell>(); // `${cid}|${branch}|${bucket}`
    const cellOf = (cid: string, br: Branch, bk: string) => {
      const key = `${cid}|${br}|${bk}`;
      let c = cells.get(key);
      if (!c) {
        c = emptyCell();
        cells.set(key, c);
      }
      return c;
    };
    const branchOfDelivery = (s: DeliveryStyle): Branch => (s === 'shinagawa' ? 'shinagawa' : 'zaitaku');
    const branchOfCost = (s: CostStyle): Branch => (s === 'buai' || s === 'jikyu' ? 'zaitaku' : 'shinagawa');

    // 自動計算(日次集計)の 取得・人件費・交通費
    const autoAppt = new Map<string, number>(); // `${date}|${cid}|${style}`
    const autoLabor = new Map<string, number>(); // `${date}|${cid}|${costStyle}`
    const autoCommute = new Map<string, number>();
    const timeeHeads = new Set<string>(); // `${date}|${cid}|${op_id}`
    // 通話料は①(シート版)と同じく、列に出す会社に紐づく分だけ数える
    const callFeeBy = new Map<string, number>(); // `${bucket}|${cid}`
    for (const r of rows) {
      const bk = bucketOf.get(r.date);
      if (!r.company_id) continue;
      if (bk) callFeeBy.set(`${bk}|${r.company_id}`, (callFeeBy.get(`${bk}|${r.company_id}`) ?? 0) + (r.talk_sec / 60) * targets.call_rate_per_min);
      const ak = `${r.date}|${r.company_id}|${deliveryStyleOf(r)}`;
      autoAppt.set(ak, (autoAppt.get(ak) ?? 0) + r.appointments);
      if (r.is_manager) continue;
      const ck = `${r.date}|${r.company_id}|${costStyleOf(r)}`;
      autoLabor.set(ck, (autoLabor.get(ck) ?? 0) + r.pay - r.commute);
      autoCommute.set(ck, (autoCommute.get(ck) ?? 0) + r.commute);
      if (r.work_style === 'timee' && (r.worked_hours > 0 || r.calls > 0)) timeeHeads.add(`${r.date}|${r.company_id}|${r.op_id}`);
    }
    // 手入力(日報/サマリ)
    const overrides = apptOverrideMap(deliveries);
    const inLabor = new Map<string, number>();
    const inCommute = new Map<string, number>();
    const delivered = new Map<string, number>(); // `${date}|${cid}|${style}`
    for (const d of deliveries) {
      for (const [cid, det] of Object.entries(d.details ?? {})) {
        for (const k of DELIVERY_STYLES) if (det.by_style[k] !== undefined) delivered.set(`${d.date}|${cid}|${k}`, det.by_style[k]!);
        for (const k of COST_STYLES) {
          if (det.labor?.[k] !== undefined) inLabor.set(`${d.date}|${cid}|${k}`, det.labor[k]!);
          if (det.commute?.[k] !== undefined) inCommute.set(`${d.date}|${cid}|${k}`, det.commute[k]!);
        }
      }
    }

    const priceCache = new Map<string, number>();
    const priceAt = (c: Company, date: DateStr) => {
      const k = `${c.id}|${date}`;
      let v = priceCache.get(k);
      if (v === undefined) {
        v = priceOn(c, date);
        priceCache.set(k, v);
      }
      return v;
    };

    for (const date of dates) {
      const bk = bucketOf.get(date);
      if (!bk) continue;
      for (const c of companies) {
        for (const st of DELIVERY_STYLES) {
          const br = branchOfDelivery(st);
          const cell = cellOf(c.id, br, bk);
          const ap = overrides.get(`${date}|${c.id}|${st}`) ?? autoAppt.get(`${date}|${c.id}|${st}`) ?? 0;
          const dl = delivered.get(`${date}|${c.id}|${st}`) ?? 0;
          cell.appts += ap;
          cell.apptsBy[st] += ap;
          cell.delivered += dl;
          cell.sales += dl * priceAt(c, date);
        }
        for (const st of COST_STYLES) {
          const br = branchOfCost(st);
          const cell = cellOf(c.id, br, bk);
          const labor = inLabor.get(`${date}|${c.id}|${st}`) ?? autoLabor.get(`${date}|${c.id}|${st}`) ?? 0;
          const commute = inCommute.get(`${date}|${c.id}|${st}`) ?? autoCommute.get(`${date}|${c.id}|${st}`) ?? 0;
          cell.cost[st] = (cell.cost[st] ?? 0) + labor;
          cell.commute += commute;
        }
        const heads = [...timeeHeads].filter((k) => k.startsWith(`${date}|${c.id}|`)).length;
        if (heads > 0) cellOf(c.id, 'shinagawa', bk).timeeFee += heads * targets.timee_fee_per_person;
      }
    }

    // 列に出す会社: 期間内に取得か納品がある会社
    const used = companies.filter((c) => buckets.some((b) => (['shinagawa', 'zaitaku'] as Branch[]).some((br) => { const x = cells.get(`${c.id}|${br}|${b.key}`); return x && (x.appts > 0 || x.delivered > 0 || Object.values(x.cost).some((v) => (v ?? 0) > 0)); })));

    const sumOver = (pick: (c: Cell) => number, cid?: string, br?: Branch) => {
      const v = zero();
      for (const b of buckets) {
        for (const c of used) {
          if (cid && c.id !== cid) continue;
          for (const bb of ['shinagawa', 'zaitaku'] as Branch[]) {
            if (br && bb !== br) continue;
            const cell = cells.get(`${c.id}|${bb}|${b.key}`);
            if (cell) v[b.key] = (v[b.key] ?? 0) + pick(cell);
          }
        }
      }
      return v;
    };
    const totalOf = (v: Record<string, number | null>) => keys.reduce((s, k) => s + (v[k] ?? 0), 0);
    const row = (key: string, label: string, v: Record<string, number | null>, fmt: PlRow['fmt'], extra: Partial<PlRow> = {}): PlRow => ({
      key,
      label,
      fmt,
      values: v,
      total: totalOf(v),
      ...extra,
    });

    const costOfCell = (c: Cell) => Object.values(c.cost).reduce((s: number, x) => s + (x ?? 0), 0) + c.commute + c.timeeFee;
    const add = (a: Record<string, number>, b: Record<string, number>) => Object.fromEntries(keys.map((k) => [k, (a[k] ?? 0) + (b[k] ?? 0)])) as Record<string, number>;
    const sub = (a: Record<string, number>, b: Record<string, number>) => Object.fromEntries(keys.map((k) => [k, (a[k] ?? 0) - (b[k] ?? 0)])) as Record<string, number>;
    /** a ÷ b の百分率(b が 0 の期間は空欄。0%の帯を並べない) */
    const pct = (a: Record<string, number>, b: Record<string, number>) =>
      Object.fromEntries(keys.map((k) => [k, (b[k] ?? 0) > 0 ? ((a[k] ?? 0) / (b[k] ?? 1)) * 100 : null])) as Record<string, number | null>;
    const sumRec = (list: Record<string, number>[]) => list.reduce((acc, v) => add(acc, v), zero());

    // ---- 会社ごとの基礎値(キャンセル・通話料は①と同じく会社ごとに丸めてから足す) ----
    const salesOf = new Map<string, Record<string, number>>();
    const cancelOf = new Map<string, Record<string, number>>();
    const salesSubOf = new Map<string, Record<string, number>>();
    const costOf = new Map<string, Record<string, number>>();
    const grossOf = new Map<string, Record<string, number>>();
    const callOf = new Map<string, Record<string, number>>();
    for (const c of used) {
      const s = sumOver((x) => x.sales, c.id);
      const x = zero();
      const cf = zero();
      for (const b of buckets) {
        x[b.key] = -Math.round(((s[b.key] ?? 0) * targets.cancel_rate) / 100);
        cf[b.key] = Math.round(callFeeBy.get(`${b.key}|${c.id}`) ?? 0);
      }
      const ssub = add(s, x);
      const cst = sumOver(costOfCell, c.id);
      salesOf.set(c.id, s);
      cancelOf.set(c.id, x);
      salesSubOf.set(c.id, ssub);
      costOf.set(c.id, cst);
      grossOf.set(c.id, sub(ssub, cst));
      callOf.set(c.id, cf);
    }
    const get = (m: Map<string, Record<string, number>>, cid: string) => m.get(cid) ?? zero();
    const sales = sumOver((x) => x.sales);
    const cancel = sumRec(used.map((c) => get(cancelOf, c.id)));
    const salesSub = add(sales, cancel);
    const cost = sumOver(costOfCell);
    const gross = sub(salesSub, cost);

    // 経費: 日報/サマリの経費画面で入れた日ごとの金額(P-22)。PL からは入力しない
    const expense = zero();
    const expenseByItem = new Map<string, Record<string, number>>();
    for (const it of items) expenseByItem.set(it.id, zero());
    for (const date of dates) {
      const bk = bucketOf.get(date);
      if (!bk) continue;
      for (const [itemId, v] of Object.entries(expenses.get(date) ?? {})) {
        expense[bk] = (expense[bk] ?? 0) + v;
        const rec = expenseByItem.get(itemId);
        if (rec) rec[bk] = (rec[bk] ?? 0) + v;
      }
    }
    const callFee = sumRec(used.map((c) => get(callOf, c.id)));
    const expenseAll = add(expense, callFee);
    const opProfit = sub(gross, expenseAll);

    // ---- アポ数(目標・取得数) ----
    const appts = sumOver((x) => x.appts);
    const targetOf = (cid: string) => {
      const v = zero();
      for (const b of buckets) v[b.key] = (targets.company_targets[cid] ?? 0) * b.dates.length;
      return v;
    };
    const apptTarget = sumRec(used.map((c) => targetOf(c.id)));
    const apptDiff = sub(appts, apptTarget);
    const visits = sumOver((x) => x.delivered);
    const styleAppts = (st: DeliveryStyle) => sumOver((x) => x.apptsBy[st]);
    const bonusRates = targets.manager_bonus_rates ?? [];
    const bonusRows = bonusRates.map((rate, i) => {
      const v = Object.fromEntries(keys.map((k) => [k, Math.round(((opProfit[k] ?? 0) * rate) / 100)])) as Record<string, number>;
      return row(`bonus_${i}`, `管理者報酬(${rate}%)`, v, 'yen', { level: 1, hint: `営業利益 × ${rate}%(現行「1日」シートの営業外経費。率は月次設定)` });
    });
    const bonusTotal = sumRec(bonusRows.map((r) => r.values as Record<string, number>));
    const ordinary = sub(opProfit, bonusTotal);

    /** 率の行(合計列は期間全体で計算し直す) */
    const rateRow = (key: string, label: string, a: Record<string, number>, b: Record<string, number>, extra: Partial<PlRow> = {}): PlRow => ({
      key,
      label,
      fmt: 'ratio',
      values: pct(a, b),
      total: totalOf(b) > 0 ? (totalOf(a) / totalOf(b)) * 100 : null,
      ...extra,
    });
    /** 会社ごとの子行 */
    const byCompany = (prefix: string, pick: (cid: string) => Record<string, number>, level: number, fmt: PlRow['fmt'] = 'yen', extra: (cid: string) => Partial<PlRow> = () => ({})) =>
      used.map((c) => row(`${prefix}_${c.id}`, c.name, pick(c.id), fmt, { level, ...extra(c.id) }));

    // ---- 階層(2026-09-10 指示):
    //  アポ数＞目標/取得数＞項目 ／ 売上＞会社＞売上・キャンセル ／ 売上原価＞アポ報酬＞会社＞出勤・交通費…
    //  ／ 粗利＞会社 ／ 経費＞会社＞項目 ／ 営業利益＞会社
    const plRows: PlRow[] = [
      row('appt', 'アポ数', appts, 'int', {
        tone: 'appt',
        hint: '目標(月次設定の会社別目標×日数)と取得数。行をクリックで内訳',
        children: [
          row('appt_target', '目標', apptTarget, 'int', {
            level: 1,
            hint: '月次設定の会社別目標(件/日) × 日数',
            children: [
              ...byCompany('tg', targetOf, 2, 'int'),
              row('appt_diff', '目標差異', apptDiff, 'int', { level: 2, better: 'プラスが良い' }),
              rateRow('appt_ratio', '目標比', appts, apptTarget, { level: 2, better: '100%以上が良い' }),
            ],
          }),
          row('appt_got', '取得数', appts, 'int', {
            level: 1,
            children: [
              row('ap_shinagawa', '予約アポ数', styleAppts('shinagawa'), 'int', { level: 2, hint: '出勤・タイミー・管理者の取得数', children: byCompany('ap_s', (cid) => sumOver((x) => x.apptsBy.shinagawa, cid), 3, 'int') }),
              row('ap_buai', '在宅歩合アポ数', styleAppts('buai'), 'int', { level: 2, children: byCompany('ap_b', (cid) => sumOver((x) => x.apptsBy.buai, cid), 3, 'int') }),
              row('ap_jikyu', '在宅時給アポ数', styleAppts('jikyu'), 'int', { level: 2, hint: '在宅時給とホワイト(不用品)', children: byCompany('ap_j', (cid) => sumOver((x) => x.apptsBy.jikyu, cid), 3, 'int') }),
              {
                key: 'share',
                label: '構成比(会社別)',
                fmt: 'ratio',
                values: Object.fromEntries(keys.map((k) => [k, (appts[k] ?? 0) > 0 ? 100 : null])),
                total: totalOf(appts) > 0 ? 100 : null,
                level: 2,
                hint: '取得数の会社シェア',
                children: used.map((c) => rateRow(`share_${c.id}`, c.name, sumOver((x) => x.appts, c.id), appts, { level: 3 })),
              },
              row('visits', '訪問数(納品件数)', visits, 'int', { level: 2, hint: '「日報/サマリ」で入れた納品件数', children: byCompany('visit', (cid) => sumOver((x) => x.delivered, cid), 3, 'int') }),
            ],
          }),
        ],
      }),
      row('sales', '売上', salesSub, 'yen', {
        tone: 'sales',
        hint: `売上(納品件数 × その日の会社単価)からキャンセル分(${targets.cancel_rate}%)を引いた小計。行をクリックで会社別`,
        children: byCompany('sl', (cid) => get(salesSubOf, cid), 1, 'yen', (cid) => ({
          children: [
            row(`sl_${cid}_s`, '売上(納品×単価)', get(salesOf, cid), 'yen', { level: 2 }),
            row(`sl_${cid}_c`, `キャンセル分(売上×${targets.cancel_rate}%)`, get(cancelOf, cid), 'yen', { level: 2 }),
          ],
        })),
      }),
      row('cost', '売上原価', cost, 'yen', {
        tone: 'cost',
        hint: '行をクリックで アポ報酬 → 会社 → 出勤・交通費・在宅歩合…',
        children: [
          row('cost_apo', 'アポ報酬', cost, 'yen', {
            level: 1,
            children: byCompany('cs', (cid) => get(costOf, cid), 2, 'yen', (cid) => ({
              children: [
                row(`cs_${cid}_shinagawa`, '出勤', sumOver((x) => x.cost.shinagawa ?? 0, cid), 'yen', { level: 3 }),
                row(`cs_${cid}_commute`, '交通費', sumOver((x) => x.commute, cid), 'yen', { level: 3 }),
                row(`cs_${cid}_buai`, '在宅歩合', sumOver((x) => x.cost.buai ?? 0, cid), 'yen', { level: 3 }),
                row(`cs_${cid}_jikyu`, '在宅時給', sumOver((x) => x.cost.jikyu ?? 0, cid), 'yen', { level: 3 }),
                row(`cs_${cid}_timee`, 'タイミー', sumOver((x) => x.cost.timee ?? 0, cid), 'yen', { level: 3 }),
                row(`cs_${cid}_fee`, 'タイミー振込手数料', sumOver((x) => x.timeeFee, cid), 'yen', { level: 3, hint: `稼働人数 × ${targets.timee_fee_per_person}円` }),
              ],
            })),
          }),
        ],
      }),
      rateRow('cost_rate', '原価率', cost, salesSub, { tone: 'cost', colorRate: true }),
      row('gross', '粗利', gross, 'yen', { tone: 'gross', hint: '売上(小計) − 売上原価。行をクリックで会社別', children: byCompany('gp', (cid) => get(grossOf, cid), 1) }),
      rateRow('gross_rate', '粗利率', gross, salesSub, { tone: 'gross', better: '高いほど良い' }),
      row('expense', '経費', expenseAll, 'yen', {
        tone: 'exp',
        hint: '「日報/サマリ」の経費画面で日ごとに入れた金額(PLからは入力しない)＋会社ごとのAj通話料',
        children: [
          ...byCompany('ex', (cid) => get(callOf, cid), 1, 'yen', (cid) => ({
            children: [row(`ex_${cid}_call`, 'Aj通話料', get(callOf, cid), 'yen', { level: 2, hint: `通話時間(分) × ${targets.call_rate_per_min}円(BlueBeanの通話時間からの自動計算)` })],
          })),
          row('ex_common', '全社共通(会社按分なし)', expense, 'yen', {
            level: 1,
            hint: '日ごとに入れた経費。会社別の按分ルールは未確定のため、この階層でまとめて表示',
            children: items.map((it) => row(`ex_common_${it.id}`, it.name, expenseByItem.get(it.id) ?? zero(), 'yen', { level: 2 })),
          }),
        ],
      }),
      row('op_profit', '営業利益', opProfit, 'yen', {
        tone: 'op',
        hint: '粗利 − 経費。行をクリックで会社別(全社共通の経費は按分せず別行)',
        children: [
          ...byCompany('op', (cid) => sub(get(grossOf, cid), get(callOf, cid)), 1),
          row('op_common', '全社共通の経費', Object.fromEntries(keys.map((k) => [k, -(expense[k] ?? 0)])) as Record<string, number>, 'yen', { level: 1 }),
        ],
      }),
      row('nonsales', '営業外売上', zero(), 'yen', { tone: 'nonsales', hint: '現行シートのサマリにある行。入力元が未確定のため 0(確認事項 P-26)' }),
      row('nonop', '営業外経費', bonusTotal, 'yen', { tone: 'nonop', hint: '管理者報酬 = 営業利益 × 率(率は「月次設定」)', children: bonusRows }),
      row('ordinary', '経常利益', ordinary, 'yen', { tone: 'ord', hint: '営業利益 − 営業外経費' }),
    ];
    return { rows: plRows, keys };
  }, [rows, deliveries, companies, targets, dates, buckets, expenses, items]);

  // 既定: 一番上の階層をすべて開く(2階層目まで見える)。利用者が開閉したらそのまま
  useEffect(() => {
    if (touched) return;
    setOpen(new Set(model.rows.filter((r) => (r.children?.length ?? 0) > 0).map((r) => r.key)));
  }, [model, touched]);

  const costTarget = targets.cost_rate_total ?? 44.5;

  /** 親階層の色は子・孫にも引き継ぐ(どのまとまりの行かを分かるようにする。2026-09-10 指示) */
  const renderRows = (list: PlRow[], tone = ''): React.ReactNode[] =>
    list.flatMap((r) => {
      const t = r.tone ?? tone;
      // 区分の見出し(現行シートの色帯)
      if (r.section) {
        return [
          <tr key={r.key} className="pl-sec">
            <td colSpan={buckets.length + 2}>
              {r.label}
              {r.hint && <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>{r.hint}</span>}
            </td>
          </tr>,
        ];
      }
      const has = (r.children?.length ?? 0) > 0;
      const isOpen = open.has(r.key);
      const level = r.level ?? 0;
      const cls = [t ? `pl-t-${t}` : '', level === 0 ? 'pl-top' : `pl-lv${Math.min(level, 3)}`, has ? 'clickable' : '', r.bold ? 'pl-bold' : ''].filter(Boolean).join(' ');
      const node = (
        <tr key={r.key} className={cls} onClick={has ? () => toggle(r.key) : undefined} aria-expanded={has ? isOpen : undefined}>
          <td className="pl-label" style={{ paddingLeft: 8 + level * 16 }}>
            {has ? (
              <span className={`chev${isOpen ? ' open' : ''}`}>
                <IconChevronRight size={12} />
              </span>
            ) : (
              <span style={{ display: 'inline-block', width: 14 }} />
            )}
            {r.hint ? <HelpTip help={{ title: r.label, what: r.hint, better: r.better ?? '—' }}>{r.label}</HelpTip> : r.label}
          </td>
          {buckets.map((b) => (
            <td key={b.key} className={`num ${r.colorRate ? costRateClass(r.values[b.key] ?? null) : ''}`}>
              {fmtCellUnit(r.values[b.key] ?? null, r.fmt, unit)}
            </td>
          ))}
          <td className="num pl-total">
            {r.colorRate ? <HelpTip help={HELP.costRate}>{fmtCellUnit(r.total, r.fmt, unit)}</HelpTip> : fmtCellUnit(r.total, r.fmt, unit)}
          </td>
        </tr>
      );
      return has && isOpen ? [node, ...renderRows(r.children!, t)] : [node];
    });

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="tool-filter" style={{ width: '100%', rowGap: 6 }}>
          <span className="hint">
            {buckets.length}列(期間の範囲を{GRAN_LABELS[gran]}に区切り) ／ 一番右が合計 ／ 金額の単位 {MONEY_UNIT_LABELS[unit]}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="txn-link" onClick={() => setOpen(new Set(model.rows.flatMap(function all(r): string[] { return [(r.children?.length ?? 0) > 0 ? r.key : '', ...(r.children ?? []).flatMap(all)].filter(Boolean); })))}>
              すべて開く
            </button>
            <button className="txn-link" onClick={() => setOpen(new Set())}>
              すべて閉じる
            </button>
          </span>
        </div>
      </div>
      <div className="table-scroll">
        <table className="fit-table pl-table">
          <thead>
            <tr>
              <th className="pl-label" style={{ minWidth: 250, width: 250 }}>
                科目 ／ 会社 ／ 項目{unit === 'yen' ? '' : `(${MONEY_UNIT_LABELS[unit]})`}
              </th>
              {buckets.map((b) => (
                <th key={b.key} className="num">
                  {b.label}
                </th>
              ))}
              <th className="num pl-total">合計</th>
            </tr>
          </thead>
          <tbody>{renderRows(model.rows)}</tbody>
        </table>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        原価率の目標 {costTarget}%。売上 = 納品件数 × その日の会社単価。取得件数・人件費・交通費は「日報/サマリ」の入力があればその値、無ければ日次集計の自動計算。
      </p>
    </>
  );
}
